import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { authFetch } from "@/lib/authFetch";

// Pages
import LandingPage from "@/pages/LandingPage";
import CFILandingPage from "@/pages/CFILandingPage";
import CFIResources from "@/pages/CFIResources";
import LoginPage from "@/pages/LoginPage";
import PartnerRegister from "@/pages/PartnerRegister";
import UpdateEmail from "@/pages/UpdateEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ProfileSettings from "@/pages/ProfileSettings";
import ReferralPartnerProfile from "@/pages/ReferralPartnerProfile";
import AdminDashboard from "@/pages/AdminDashboard";
import TeamDashboard from "@/pages/TeamDashboard";
import ClientDashboard from "@/pages/ClientDashboard";
import ClientDetail from "@/pages/ClientDetail";
import UserManagement from "@/pages/UserManagement";
import AffiliateManagement from "@/pages/AffiliateManagement";
import AffiliateHistory from "@/pages/AffiliateHistory";
import ProblemLinks from "@/pages/ProblemLinks";
import { LinkJobCenter } from "@/components/LinkJobCenter";
import AccessRequests from "@/pages/AccessRequests";
import ApiKeysPage from "@/pages/ApiKeysPage";
import AllClients from "@/pages/AllClients";
import EmbedPage from "@/pages/EmbedPage";
import AddClientPage from "@/pages/AddClientPage";
import PortalTraining from "@/pages/PortalTraining";
import UpdateBilling from "@/pages/UpdateBilling";
import ClientAIAssistant from "@/pages/ClientAIAssistant";


import ErrorBoundary from "@/components/ErrorBoundary";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate("/login");
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await authFetch(`${API}/auth/session`, {
          method: "GET",
          headers: {
            "X-Session-ID": sessionId,
          },
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Authentication failed");
        }

        const userData = await response.json();
        
        const role = userData.role;
        if (role === "CREDITFIRMINC_ADMIN" || role === "BETENBOUGH_ADMIN" || role === "BH_ADMIN" || role === "BH_LOCATION_ADMIN") {
          navigate("/admin/dashboard", { state: { user: userData } });
        } else if (role === "BETENBOUGH_TEAM_MEMBER" || role === "REFERRAL_PARTNER" || role === "PREFERRED_LENDER") {
          navigate("/team/dashboard", { state: { user: userData } });
        } else {
          navigate("/client/dashboard", { state: { user: userData } });
        }
      } catch (error) {
        console.error("Auth error:", error);
        navigate("/login");
      }
    };

    processAuth();
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
        <p className="text-slate-600">Authenticating...</p>
      </div>
    </div>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated, setUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if user was passed via navigation state (from login)
  useEffect(() => {
    if (location.state?.user && !user) {
      setUser(location.state.user);
    }
  }, [location.state, user, setUser]);

  // Get the effective user (from context or from navigation state)
  const effectiveUser = user || location.state?.user;
  const effectiveAuth = isAuthenticated || !!location.state?.user;

  useEffect(() => {
    if (!loading && !effectiveAuth) {
      navigate("/login", { state: { from: location } });
    }
  }, [loading, effectiveAuth, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!effectiveAuth) {
    return null;
  }

  if (allowedRoles && effectiveUser && !allowedRoles.includes(effectiveUser.role)) {
    if (effectiveUser.role === "CREDITFIRMINC_ADMIN" || effectiveUser.role === "BETENBOUGH_ADMIN" || effectiveUser.role === "BH_ADMIN" || effectiveUser.role === "BH_LOCATION_ADMIN" || effectiveUser.role === "CREDIT_COUNSELOR") {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (effectiveUser.role === "BETENBOUGH_TEAM_MEMBER" || effectiveUser.role === "REFERRAL_PARTNER" || effectiveUser.role === "PREFERRED_LENDER") {
      return <Navigate to="/team/dashboard" replace />;
    } else {
      return <Navigate to="/client/dashboard" replace />;
    }
  }

  return children;
};

// Main Router
function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Navigate to="/cfi" replace />} />
        <Route path="/refer" element={<LandingPage />} />
        <Route path="/cfi" element={<CFILandingPage />} />
        <Route path="/resources" element={<CFIResources />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/partner-login" element={<Navigate to="/login" replace />} />
        <Route path="/register" element={<PartnerRegister />} />
        <Route path="/update-email" element={<UpdateEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

      {/* Admin Routes */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={["CREDITFIRMINC_ADMIN", "BETENBOUGH_ADMIN", "BH_ADMIN", "BH_LOCATION_ADMIN", "CREDIT_COUNSELOR"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute allowedRoles={["CREDITFIRMINC_ADMIN", "BETENBOUGH_ADMIN", "BH_ADMIN", "BH_LOCATION_ADMIN", "CREDIT_COUNSELOR"]}>
            <ProfileSettings isAdmin={true} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/add-client"
        element={
          <ProtectedRoute allowedRoles={["CREDITFIRMINC_ADMIN", "BETENBOUGH_ADMIN", "BH_ADMIN", "BH_LOCATION_ADMIN", "CREDIT_COUNSELOR"]}>
            <AddClientPage isAdmin={true} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients"
        element={
          <ProtectedRoute allowedRoles={["CREDITFIRMINC_ADMIN", "BETENBOUGH_ADMIN", "BH_ADMIN", "BH_LOCATION_ADMIN", "CREDIT_COUNSELOR"]}>
            <AllClients isAdmin={true} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients/:id"
        element={
          <ProtectedRoute allowedRoles={["CREDITFIRMINC_ADMIN", "BETENBOUGH_ADMIN", "BH_ADMIN", "BH_LOCATION_ADMIN", "CREDIT_COUNSELOR"]}>
            <ClientDetail isAdmin={true} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={["CREDITFIRMINC_ADMIN", "BETENBOUGH_ADMIN", "BH_ADMIN", "CREDIT_COUNSELOR"]}>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/affiliates"
        element={
          <ProtectedRoute allowedRoles={["CREDITFIRMINC_ADMIN"]}>
            <AffiliateManagement />
          </ProtectedRoute>
        }
      />
      <Route path="/admin/affiliates/history" element={<ProtectedRoute allowedRoles={["CREDITFIRMINC_ADMIN"]}><AffiliateHistory /></ProtectedRoute>} />
      <Route path="/admin/affiliates/problems" element={<ProtectedRoute allowedRoles={["CREDITFIRMINC_ADMIN"]}><ProblemLinks /></ProtectedRoute>} />
      <Route path="/admin/lenders" element={<Navigate to="/admin/affiliates" replace />} />
      <Route
        path="/admin/access-requests"
        element={
          <ProtectedRoute allowedRoles={["CREDITFIRMINC_ADMIN", "BETENBOUGH_ADMIN", "BH_ADMIN"]}>
            <AccessRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/api-keys"
        element={
          <ProtectedRoute allowedRoles={["CREDITFIRMINC_ADMIN", "BETENBOUGH_ADMIN"]}>
            <ApiKeysPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/resources"
        element={
          <ProtectedRoute allowedRoles={["CREDITFIRMINC_ADMIN", "BETENBOUGH_ADMIN", "BH_ADMIN", "BH_LOCATION_ADMIN", "CREDIT_COUNSELOR"]}>
            <CFIResources />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/training"
        element={
          <ProtectedRoute allowedRoles={["CREDITFIRMINC_ADMIN", "BETENBOUGH_ADMIN", "BH_ADMIN", "BH_LOCATION_ADMIN", "CREDIT_COUNSELOR"]}>
            <PortalTraining isAdmin={true} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/booking"
        element={
          <ProtectedRoute allowedRoles={["CREDITFIRMINC_ADMIN", "BETENBOUGH_ADMIN", "BH_ADMIN", "BH_LOCATION_ADMIN", "CREDIT_COUNSELOR"]}>
            <EmbedPage type="booking" isAdmin={true} />
          </ProtectedRoute>
        }
      />

      {/* Team/Referral Partner Routes */}
      <Route
        path="/team/dashboard"
        element={
          <ProtectedRoute allowedRoles={["BETENBOUGH_TEAM_MEMBER", "REFERRAL_PARTNER", "PREFERRED_LENDER"]}>
            <TeamDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team/add-client"
        element={
          <ProtectedRoute allowedRoles={["BETENBOUGH_TEAM_MEMBER", "REFERRAL_PARTNER", "PREFERRED_LENDER"]}>
            <AddClientPage isAdmin={false} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team/clients"
        element={
          <ProtectedRoute allowedRoles={["BETENBOUGH_TEAM_MEMBER", "REFERRAL_PARTNER", "PREFERRED_LENDER"]}>
            <AllClients isAdmin={false} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team/clients/:id"
        element={
          <ProtectedRoute allowedRoles={["BETENBOUGH_TEAM_MEMBER", "REFERRAL_PARTNER", "PREFERRED_LENDER"]}>
            <ClientDetail isAdmin={false} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team/settings"
        element={
          <ProtectedRoute allowedRoles={["BETENBOUGH_TEAM_MEMBER", "REFERRAL_PARTNER", "PREFERRED_LENDER"]}>
            <ProfileSettings isAdmin={false} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team/profile"
        element={
          <ProtectedRoute allowedRoles={["BETENBOUGH_TEAM_MEMBER", "REFERRAL_PARTNER", "PREFERRED_LENDER"]}>
            <ReferralPartnerProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team/resources"
        element={
          <ProtectedRoute allowedRoles={["BETENBOUGH_TEAM_MEMBER", "REFERRAL_PARTNER", "PREFERRED_LENDER"]}>
            <CFIResources />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team/training"
        element={
          <ProtectedRoute allowedRoles={["BETENBOUGH_TEAM_MEMBER", "REFERRAL_PARTNER", "PREFERRED_LENDER"]}>
            <PortalTraining isAdmin={false} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team/booking"
        element={
          <ProtectedRoute allowedRoles={["BETENBOUGH_TEAM_MEMBER", "REFERRAL_PARTNER", "PREFERRED_LENDER"]}>
            <EmbedPage type="booking" isAdmin={false} />
          </ProtectedRoute>
        }
      />

      {/* Client Routes */}
      <Route
        path="/client/dashboard"
        element={
          <ProtectedRoute allowedRoles={["CLIENT"]}>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/assistant"
        element={
          <ProtectedRoute allowedRoles={["CLIENT"]}>
            <ClientAIAssistant />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/billing"
        element={
          <ProtectedRoute allowedRoles={["CLIENT"]}>
            <UpdateBilling />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/resources"
        element={
          <ProtectedRoute allowedRoles={["CLIENT"]}>
            <CFIResources />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/training"
        element={
          <ProtectedRoute allowedRoles={["CLIENT"]}>
            <Navigate to="/client/dashboard" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/settings"
        element={
          <ProtectedRoute allowedRoles={["CLIENT"]}>
            <ProfileSettings isAdmin={false} />
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/cfi" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <ThemeProvider>
              <AppRouter />
              <Toaster position="top-right" richColors />
              <LinkJobCenter />
            </ThemeProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
